import { useEffect, useRef, useState, type PointerEvent } from "react";
import { hasInk, paint, PAPER, readDraft, SIZE, type Mark, type Point, type Tool } from "./drawing/model";
import { FIT, clampCamera, pinchCamera, zoomAt, type Camera } from "./drawing/camera";
import { Icon, type IconName } from "./drawing/Icon";
import "./drawing/drawing.css";

export const COLORS = [
  "#202124",
  "#ffffff",
  "#e74747",
  "#ff9933",
  "#f6ce38",
  "#38a169",
  "#3285d5",
  "#9b65cf",
];
const LABELS: Record<Tool, string> = {
  pen: "Олівець",
  eraser: "Гумка",
  fill: "Заливка",
  picker: "Піпетка",
  line: "Лінія",
  rectangle: "Прямокутник",
  ellipse: "Коло",
  hand: "Рухати",
};
interface Props {
  disabled: boolean;
  busy: boolean;
  onSave: (pngDataUrl: string) => void;
  draftKey?: string;
  word?: string;
  unlockGallery?: boolean;
}
interface Sample {
  color: string;
  x: number;
  y: number;
  image: string;
}

export function DrawCanvas({ disabled, busy, onSave, draftKey, word, unlockGallery = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const base = useRef<HTMLCanvasElement | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const [marks, setMarks] = useState<Mark[]>(() => {
    try {
      return draftKey ? readDraft(localStorage.getItem(draftKey)) : [];
    } catch {
      return [];
    }
  });
  const [redo, setRedo] = useState<Mark[]>([]);
  const [color, setColor] = useState(COLORS[0]!);
  const [hex, setHex] = useState(COLORS[0]!);
  const [recent, setRecent] = useState<string[]>([]);
  const [size, setSize] = useState(8);
  const [tool, setTool] = useState<Tool>("pen");
  const [camera, setCamera] = useState<Camera>(FIT);
  const cameraRef = useRef(camera);
  const [expanded, setExpanded] = useState(false);
  const [panel, setPanel] = useState<"size" | "color" | "more" | null>(null);
  const [confirm, setConfirm] = useState<"clear" | "publish" | null>(null);
  const [draftSaved, setDraftSaved] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [sample, setSample] = useState<Sample | null>(null);
  const sampleRef = useRef<Sample | null>(null);
  const current = useRef<Mark | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<{ camera: Camera; points: [Point, Point] } | null>(null);
  const panning = useRef<Point | null>(null);
  const gesturing = useRef(false);
  const space = useRef(false);
  const frame = useRef<number | null>(null);
  const locked = disabled || busy;
  const ink = hasInk(marks);
  const setView = (next: Camera) => {
    cameraRef.current = next;
    setCamera(next);
  };
  const repaint = () => {
    const g = canvasRef.current?.getContext("2d");
    if (!g || !base.current) return;
    g.drawImage(base.current, 0, 0);
    if (current.current && current.current.tool !== "fill") paint(g, [current.current], false);
  };
  useEffect(() => {
    base.current ??= document.createElement("canvas");
    base.current.width = base.current.height = SIZE;
    const g = base.current.getContext("2d");
    if (g) paint(g, marks);
    repaint();
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(marks));
      setDraftSaved(true);
    } catch {
      setDraftSaved(false);
    }
  }, [marks, draftKey]);
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const wheel = (e: WheelEvent) => {
      if (locked || pointers.current.size) return;
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      const delta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.width : 1);
      setView(
        zoomAt(
          cameraRef.current,
          cameraRef.current.scale * Math.exp(-delta * 0.005),
          { x: e.clientX - rect.left, y: e.clientY - rect.top },
          rect.width,
        ),
      );
    };
    const resize = new ResizeObserver(() => setView(FIT));
    resize.observe(stage);
    stage.addEventListener("wheel", wheel, { passive: false });
    const up = (e: KeyboardEvent) => {
      if (e.code === "Space") space.current = false;
    };
    const blur = () => {
      space.current = false;
    };
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      stage.removeEventListener("wheel", wheel);
      resize.disconnect();
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, [locked]);
  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (confirm) setConfirm(null);
        else if (panel) setPanel(null);
        else setExpanded(false);
      }
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [expanded, confirm, panel]);
  useEffect(() => {
    if (!confirm) return;
    const previous = document.activeElement;
    const buttons = confirmRef.current?.querySelectorAll<HTMLButtonElement>("button");
    const first = buttons?.[0],
      last = buttons?.[buttons.length - 1];
    last?.focus();
    const keys = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", keys);
    return () => {
      document.removeEventListener("keydown", keys);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [confirm]);
  const selectColor = (c: string) => {
    setColor(c);
    setHex(c);
    setRecent((prev) => [c, ...prev.filter((v) => v !== c)].slice(0, 6));
    if (tool === "eraser" || tool === "picker" || tool === "hand") setTool("pen");
  };
  const local = (e: { clientX: number; clientY: number }): Point => {
    const rect = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const point = (e: { clientX: number; clientY: number }): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(SIZE, ((e.clientX - rect.left) * SIZE) / rect.width)),
      y: Math.max(0, Math.min(SIZE, ((e.clientY - rect.top) * SIZE) / rect.height)),
    };
  };
  const pick = (e: { clientX: number; clientY: number }) => {
    const p = point(e),
      pos = local(e);
    const x = Math.min(SIZE - 1, Math.floor(p.x)),
      y = Math.min(SIZE - 1, Math.floor(p.y));
    const g = base.current?.getContext("2d");
    if (!g || !base.current) return;
    const pixel = g.getImageData(x, y, 1, 1).data;
    const c = `#${[pixel[0]!, pixel[1]!, pixel[2]!].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
    const magnifier = document.createElement("canvas");
    magnifier.width = magnifier.height = 80;
    const mg = magnifier.getContext("2d")!;
    mg.fillStyle = PAPER;
    mg.fillRect(0, 0, 80, 80);
    mg.imageSmoothingEnabled = false;
    mg.drawImage(base.current, x - 10, y - 10, 20, 20, 0, 0, 80, 80);
    const next = { color: c, x: pos.x, y: pos.y, image: magnifier.toDataURL() };
    sampleRef.current = next;
    setSample(next);
  };
  const cancelMark = () => {
    current.current = null;
    sampleRef.current = null;
    setSample(null);
    repaint();
  };
  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (locked || (e.button !== 0 && e.button !== 1)) return;
    e.preventDefault();
    e.currentTarget.focus({ preventScroll: true });
    setPanel(null);
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    setDrawing(true);
    if (pointers.current.size >= 2) {
      cancelMark();
      panning.current = null;
      gesturing.current = true;
      gesture.current = {
        camera: cameraRef.current,
        points: [...pointers.current.values()].slice(0, 2) as [Point, Point],
      };
      return;
    }
    if (gesturing.current) return;
    if (tool === "hand" || space.current || e.button === 1) {
      panning.current = local(e);
      return;
    }
    if (tool === "picker") {
      pick(e);
      return;
    }
    current.current = {
      tool,
      color,
      size: tool === "eraser" ? Math.min(96, size * 2) : size,
      points: [point(e)],
    };
    repaint();
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    const pos = local(e);
    pointers.current.set(e.pointerId, pos);
    const width = stageRef.current!.getBoundingClientRect().width;
    if (gesturing.current) {
      if (gesture.current && pointers.current.size >= 2)
        setView(
          pinchCamera(
            gesture.current.camera,
            gesture.current.points,
            [...pointers.current.values()].slice(0, 2) as [Point, Point],
            width,
          ),
        );
      return;
    }
    if (panning.current) {
      const c = cameraRef.current;
      setView(
        clampCamera({ ...c, x: c.x + pos.x - panning.current.x, y: c.y + pos.y - panning.current.y }, width),
      );
      panning.current = pos;
      return;
    }
    if (tool === "picker") {
      pick(e);
      return;
    }
    const active = current.current;
    if (!active) return;
    const events =
      typeof e.nativeEvent.getCoalescedEvents === "function" ? e.nativeEvent.getCoalescedEvents() : [];
    for (const event of events.length ? events : [e]) {
      const p = point(event);
      if (active.tool === "pen" || active.tool === "eraser") {
        const last = active.points.at(-1)!;
        if ((last.x - p.x) ** 2 + (last.y - p.y) ** 2 >= 0.5) active.points.push(p);
      } else active.points = [active.points[0]!, p];
    }
    if (frame.current === null)
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        repaint();
      });
  };
  const end = (e: PointerEvent<HTMLDivElement>, cancel = false) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
    if (!gesturing.current && !cancel) {
      if (sampleRef.current) {
        pick(e);
        selectColor(sampleRef.current!.color);
      }
      const active = current.current;
      if (active) {
        if (active.tool === "fill") active.points = [point(e)];
        else active.points.push(point(e));
        setMarks((prev) => [...prev, active]);
        setRedo([]);
      }
    }
    cancelMark();
    if (pointers.current.size < 2) gesture.current = null;
    else
      gesture.current = {
        camera: cameraRef.current,
        points: [...pointers.current.values()].slice(0, 2) as [Point, Point],
      };
    if (!pointers.current.size) {
      gesturing.current = false;
      panning.current = null;
      setDrawing(false);
    }
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const undo = () => {
    const last = marks.at(-1);
    if (!last || locked || drawing) return;
    setMarks(marks.slice(0, -1));
    setRedo([...redo, last]);
  };
  const redoMark = () => {
    const next = redo.at(-1);
    if (!next || locked || drawing) return;
    setMarks([...marks, next]);
    setRedo(redo.slice(0, -1));
  };
  const publish = () => {
    if (locked || drawing || !ink || !base.current) return;
    onSave(base.current.toDataURL("image/png"));
    setConfirm(null);
  };
  const zoom = (factor: number) => {
    const w = stageRef.current!.getBoundingClientRect().width;
    setView(zoomAt(cameraRef.current, cameraRef.current.scale * factor, { x: w / 2, y: w / 2 }, w));
  };
  const chooseTool = (next: Tool) => {
    setTool(next);
    setPanel(tool === next && ["pen", "eraser"].includes(next) ? (panel === "size" ? null : "size") : null);
  };
  const iconButton = (name: IconName, label: string, action: () => void, off = false) => (
    <button
      type="button"
      className="drawing-icon-button"
      aria-label={label}
      title={label}
      onClick={action}
      disabled={locked || drawing || off}
    >
      <Icon name={name} />
    </button>
  );
  return (
    <div
      className={`drawing-studio ${expanded ? "drawing-studio-expanded" : ""}`}
      onKeyDown={(e) => {
        if (e.target instanceof HTMLInputElement) return;
        if (e.code === "Space" && e.target === stageRef.current) {
          e.preventDefault();
          space.current = true;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
          e.preventDefault();
          if (e.shiftKey) redoMark();
          else undo();
        }
      }}
    >
      <div className="drawing-topline">
        <div className="drawing-word">
          <small>Намалюй без літер</small>
          <strong>{word ?? "Твій ескіз"}</strong>
        </div>
        <div className="drawing-top-actions">
          {iconButton("undo", "Скасувати останню дію", undo, !marks.length)}
          {iconButton("redo", "Повернути скасовану дію", redoMark, !redo.length)}
          {iconButton(expanded ? "close" : "expand", expanded ? "Згорнути" : "На весь екран", () =>
            setExpanded(!expanded),
          )}
        </div>
      </div>
      <div
        className="drawing-stage"
        ref={stageRef}
        tabIndex={0}
        role="group"
        aria-label="Полотно для малювання. Два пальці — масштаб. На комп’ютері — колесо, пробіл і перетягування."
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={(e) => end(e)}
        onPointerCancel={(e) => end(e, true)}
        onLostPointerCapture={(e) => end(e, true)}
        style={{ cursor: tool === "hand" ? "grab" : tool === "picker" ? "crosshair" : "crosshair" }}
      >
        <canvas
          ref={canvasRef}
          width={SIZE}
          height={SIZE}
          className="drawing-paper"
          aria-label="Малюнок"
          style={{ transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.scale})` }}
        />
        {sample && (
          <>
            <span className="drawing-sample-target" style={{ left: sample.x, top: sample.y }} />
            <div
              className="drawing-loupe"
              style={{
                left: Math.max(52, Math.min((stageRef.current?.clientWidth ?? 300) - 52, sample.x)),
                top: sample.y < 125 ? sample.y + 35 : sample.y - 125,
                borderColor: sample.color,
              }}
            >
              <img src={sample.image} alt="Збільшена ділянка під пальцем" />
              <span className="drawing-loupe-cross" />
              <span className="drawing-loupe-color" style={{ background: sample.color }} />
              <output>{sample.color.toUpperCase()}</output>
            </div>
          </>
        )}
      </div>
      <div className="drawing-viewbar">
        <span>
          {tool === "picker" ? "Веди пальцем · відпусти, щоб обрати" : "Два пальці — масштаб і рух"}
        </span>
        <div>
          {iconButton("minus", "Зменшити полотно", () => zoom(1 / 1.5), camera.scale <= 1)}
          <button
            type="button"
            onClick={() => setView(FIT)}
            disabled={drawing}
            aria-label="Повернути масштаб 100%"
          >
            {Math.round(camera.scale * 100)}%
          </button>
          {iconButton("plus", "Збільшити полотно", () => zoom(1.5), camera.scale >= 6)}
        </div>
      </div>
      <div className="drawing-dock-wrap">
        {panel && (
          <section
            className="drawing-panel"
            aria-label={
              panel === "color" ? "Кольори" : panel === "size" ? "Товщина інструмента" : "Інші інструменти"
            }
          >
            <div className="drawing-panel-title">
              <strong>{panel === "color" ? "Колір" : panel === "size" ? LABELS[tool] : "Інструменти"}</strong>
              <button
                type="button"
                className="drawing-icon-button"
                aria-label="Закрити налаштування"
                onClick={() => setPanel(null)}
              >
                <Icon name="close" />
              </button>
            </div>
            {panel === "size" && (
              <div className="drawing-size">
                <span className="drawing-brush-preview">
                  <i
                    style={{
                      width: Math.min(32, size),
                      height: Math.min(32, size),
                      background: tool === "eraser" ? PAPER : color,
                    }}
                  />
                </span>
                <label htmlFor="drawing-size">Товщина</label>
                <input
                  id="drawing-size"
                  type="range"
                  min={1}
                  max={48}
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
                <output>{size}</output>
              </div>
            )}
            {panel === "color" && (
              <>
                <div className="drawing-colors">
                  {COLORS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      aria-label={`Колір ${c}`}
                      aria-pressed={color === c}
                      onClick={() => selectColor(c)}
                    >
                      <span style={{ background: c }} />
                    </button>
                  ))}
                </div>
                {recent.length > 0 && (
                  <div className="drawing-recent">
                    <small>Останні</small>
                    {recent.map((c) => (
                      <button
                        type="button"
                        key={c}
                        aria-label={`Недавній колір ${c}`}
                        onClick={() => selectColor(c)}
                      >
                        <span style={{ background: c }} />
                      </button>
                    ))}
                  </div>
                )}
                <div className="drawing-custom-color">
                  <label>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => selectColor(e.target.value)}
                      aria-label="Обрати будь-який колір"
                    />
                    Свій колір
                  </label>
                  <input
                    aria-label="HEX кольору"
                    className="drawing-hex"
                    value={hex}
                    maxLength={7}
                    spellCheck={false}
                    onChange={(e) => {
                      setHex(e.target.value);
                      if (/^#[\da-f]{6}$/i.test(e.target.value)) selectColor(e.target.value);
                    }}
                    onBlur={() => setHex(color)}
                  />
                </div>
                <button
                  type="button"
                  className="drawing-picker-button"
                  onClick={() => {
                    setTool("picker");
                    setPanel(null);
                  }}
                >
                  <Icon name="picker" />
                  Взяти колір з малюнка
                </button>
              </>
            )}
            {panel === "more" && (
              <>
                <div className="drawing-extra-tools">
                  {(["line", "rectangle", "ellipse", "hand", "picker"] as const).map((t) => (
                    <button type="button" key={t} aria-pressed={tool === t} onClick={() => chooseTool(t)}>
                      <Icon name={t} />
                      {LABELS[t]}
                    </button>
                  ))}
                </div>
                <div className="drawing-size">
                  <label htmlFor="shape-size">Товщина</label>
                  <input
                    id="shape-size"
                    type="range"
                    min={1}
                    max={48}
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                  />
                  <output>{size}</output>
                </div>
                <p className="drawing-desktop-tip">
                  На комп’ютері: колесо — масштаб, пробіл + рух — пересунути, Ctrl/⌘ Z — скасувати.
                </p>
                <button
                  type="button"
                  className="drawing-clear"
                  disabled={!ink}
                  onClick={() => {
                    setPanel(null);
                    setConfirm("clear");
                  }}
                >
                  Очистити полотно
                </button>
              </>
            )}
          </section>
        )}
        <fieldset disabled={locked || drawing} className="drawing-dock" aria-label="Інструменти">
          {(["pen", "eraser", "fill"] as const).map((t) => (
            <button
              type="button"
              key={t}
              aria-label={LABELS[t]}
              aria-pressed={tool === t}
              onClick={() => chooseTool(t)}
            >
              <Icon name={t} />
              <span>{LABELS[t]}</span>
            </button>
          ))}
          <button
            type="button"
            aria-label="Колір і піпетка"
            aria-expanded={panel === "color"}
            onClick={() => setPanel(panel === "color" ? null : "color")}
          >
            <i className="drawing-color-dot" style={{ background: color }} />
            <span>Колір</span>
          </button>
          <button
            type="button"
            aria-label="Інші інструменти"
            aria-expanded={panel === "more"}
            aria-pressed={!["pen", "eraser", "fill"].includes(tool)}
            onClick={() => setPanel(panel === "more" ? null : "more")}
          >
            <Icon name={["pen", "eraser", "fill"].includes(tool) ? "more" : tool} />
            <span>Ще</span>
          </button>
        </fieldset>
      </div>
      <div className="drawing-footer">
        <p role="status">
          {draftKey && !draftSaved
            ? "Не вдалось зберегти чернетку. Не закривай гру."
            : tool === "pen" || tool === "eraser"
              ? "Натисни інструмент ще раз, щоб змінити товщину"
              : tool === "fill"
                ? "Торкнись замкненої ділянки, щоб залити її кольором"
                : draftKey
                  ? "Чернетка збережена на цьому пристрої"
                  : "Без літер і цифр — тільки малюнок"}
        </p>
        <button
          type="button"
          className="drawing-publish"
          disabled={locked || drawing || !ink}
          onClick={() => {
            setPanel(null);
            setConfirm("publish");
          }}
        >
          {busy ? "Публікуємо…" : "Готово · показати друзям"}
        </button>
      </div>
      {confirm && (
        <div
          ref={confirmRef}
          className="drawing-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="drawing-confirm-title"
        >
          <div>
            <h2 id="drawing-confirm-title">
              {confirm === "clear" ? "Почати з чистого аркуша?" : "Показуємо друзям?"}
            </h2>
            <p>
              {confirm === "clear"
                ? "Малюнок можна повернути кнопкою «Назад»."
                : unlockGallery
                  ? "Після публікації малюнок уже не змінити. Натомість відкриються роботи друзів."
                  : "Друзі побачать малюнок і зможуть вгадати твоє слово."}
            </p>
            <button
              type="button"
              className="drawing-publish"
              disabled={locked}
              onClick={() => {
                if (confirm === "publish") publish();
                else {
                  setMarks([...marks, { tool: "clear", color: PAPER, size: 1, points: [] }]);
                  setRedo([]);
                  setConfirm(null);
                }
              }}
            >
              {confirm === "clear" ? "Очистити" : "Опублікувати"}
            </button>
            <button type="button" className="drawing-text-button" onClick={() => setConfirm(null)}>
              Ще помалюю
            </button>
          </div>
        </div>
      )}{" "}
    </div>
  );
}
